/**
 * useUpdateChildDifficulty hook
 *
 * Optimistic React Query mutation for setting a child's English difficulty tier
 * from the ChildCard compact dropdown (T10). Calls setChildDifficulty (TS-007 T2,
 * API-003) and writes the new tier into the ['parent','dashboard'] cache
 * immediately so the dropdown reflects the change with zero perceived latency,
 * then rolls back to the exact previous snapshot if the server rejects the write.
 *
 * Implements TS-007 T4 (AC-TS007.5).
 * Modelled on useChildMutations' rename mutation (hooks/useChildMutations.ts) for
 * scaffolding (useMutation + useQueryClient + invalidateQueries), but the
 * onMutate/onError/onSettled optimistic-rollback triad is net-new — renameChild's
 * mutation is NOT optimistic today.
 *
 * KB enforcement:
 *   TO-LL-036 — the optimistic write is never left standing if the server rejects
 *               it: onError restores the EXACT previous cache snapshot (never a
 *               re-derived value) and the error is exposed raw (never swallowed
 *               or hidden behind a fallback). onSettled invalidates so the
 *               authoritative server value reconciles regardless of outcome.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setChildDifficulty } from '@/services/children';
import type { AuthError } from '@/types/auth';
import type { DifficultyTier, ParentDashboardResponse } from '@/types/coins';

/** Variables for the difficulty mutation: childId + new tier */
export interface UpdateChildDifficultyVariables {
  /** The child's userId (Cognito sub) */
  childId: string;
  /** New learning level — allowlist-bound by the <select> options (T10) */
  difficultyTier: DifficultyTier;
}

/** Context passed from onMutate to onError for rollback */
interface DifficultyMutationContext {
  previousDashboard: ParentDashboardResponse | undefined;
}

/**
 * Return type for useUpdateChildDifficulty hook
 */
export interface UseUpdateChildDifficultyReturn {
  /** Trigger the optimistic difficulty mutation */
  updateDifficulty: (vars: UpdateChildDifficultyVariables) => void;
  /** True while the PATCH is in flight */
  isUpdating: boolean;
  /**
   * Error from a failed update, or null. Exposed so ChildCard can show an inline
   * retry message near the dropdown. Implements TO-LL-036: error is never swallowed.
   */
  error: AuthError | null;
  /** Reset the mutation state (clear error) */
  reset: () => void;
}

/**
 * Hook to optimistically set a child's difficulty tier with rollback on failure.
 *
 * onMutate: cancels in-flight ['parent','dashboard'] queries (so a stale in-flight
 *   response cannot clobber a newer optimistic write on rapid re-selects), snapshots
 *   the current cache, and writes the new difficultyTier onto the matching child
 *   (matched by userId — never array index).
 * onError: restores the EXACT previous snapshot from context (rollback); error
 *   surfaces via mutation.error for the caller to render inline.
 * onSettled: invalidates ['parent','dashboard'] so the authoritative server value
 *   reconciles whether the mutation succeeded or failed.
 */
export function useUpdateChildDifficulty(): UseUpdateChildDifficultyReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    { userId: string; displayName: string; difficultyTier: string },
    AuthError,
    UpdateChildDifficultyVariables,
    DifficultyMutationContext
  >({
    mutationFn: ({ childId, difficultyTier }) => setChildDifficulty(childId, difficultyTier),

    onMutate: async ({ childId, difficultyTier }) => {
      await queryClient.cancelQueries({ queryKey: ['parent', 'dashboard'] });

      const previousDashboard = queryClient.getQueryData<ParentDashboardResponse>([
        'parent',
        'dashboard',
      ]);

      if (previousDashboard) {
        queryClient.setQueryData<ParentDashboardResponse>(['parent', 'dashboard'], {
          ...previousDashboard,
          children: previousDashboard.children.map((child) =>
            child.userId === childId ? { ...child, difficultyTier } : child
          ),
        });
      }

      // Returned as context — used verbatim (never re-derived) by onError for rollback.
      return { previousDashboard };
    },

    onError: (_err, _vars, context) => {
      // TO-LL-036: restore the EXACT previous snapshot — never a re-derived value.
      if (context?.previousDashboard) {
        queryClient.setQueryData(['parent', 'dashboard'], context.previousDashboard);
      }
    },

    onSettled: () => {
      // Reconcile with the authoritative server value regardless of outcome.
      queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
    },
  });

  return {
    updateDifficulty: (vars) => mutation.mutate(vars),
    isUpdating: mutation.isPending,
    error: (mutation.error as AuthError | null) ?? null,
    reset: mutation.reset,
  };
}
